import type { CanonicalGameFrame } from "./canonicalGameSimulation";
import { applyGameSequencePresentation, GameSequenceDirector, GAME_SEQUENCE_TIMINGS, type GameSequenceFrame } from "./gameSequence";
export type HighlightShot = "reading" | "hero" | "bridge" | "game" | "self" | "expand" | "complete";
export interface HighlightFrame {
  shot: HighlightShot;
  elapsed: number;
  local: number;
  fade: number;
  expansion: number;
  locked: boolean;
}
const clamp = (v: number): number => Math.max(0, Math.min(1, v));
const smooth = (v: number): number => { const t = clamp(v); return t * t * (3 - 2 * t); };
export const HIGHLIGHT_CUTS = [0, 3800, 11800, 20600, 31000, 34800, 37400] as const;
export const HIGHLIGHT_GAME_START = GAME_SEQUENCE_TIMINGS.transferAt - 700;
export function shouldPresentPendingSiteHeading(siteSelected: boolean, retainedGame: boolean, gameBox: { top: number; bottom: number } | null, viewportHeight: number): boolean {
  return siteSelected && retainedGame && viewportHeight > 0 && gameBox !== null
    && (gameBox.bottom <= 0 || gameBox.top >= viewportHeight);
}
export function highlightCopyPresence(state: HighlightFrame): number {
  if (state.shot === "expand") return 0;
  if (state.shot === "self") return 1 - smooth((state.local - .55) / .45);
  return 1;
}
/** Real time, not scrubbed text: every shot completes even when scrolling stops. */
export function sampleHighlight(elapsed: number): HighlightFrame {
  const t = Math.max(0, elapsed);
  const shots: HighlightShot[] = ["reading", "hero", "bridge", "game", "self", "expand", "complete"];
  let index = 0;
  while (index < HIGHLIGHT_CUTS.length - 1 && t >= HIGHLIGHT_CUTS[index + 1]!) index++;
  const start = HIGHLIGHT_CUTS[index]!;
  const end = HIGHLIGHT_CUTS[index + 1] ?? start + 1;
  const local = clamp((t - start) / (end - start));
  const isCut = index >= 1 && index <= 3;
  const fade = isCut ? smooth((t - start) / 500) * smooth((end - t) / 500) : 1;
  return { shot: shots[index]!, elapsed: t, local, fade, expansion: index < 5 ? 0 : index === 5 ? smooth(local) : 1, locked: index < 6 };
}

export interface HighlightPresentation extends HighlightFrame {
  game?: CanonicalGameFrame;
  gameSequence?: GameSequenceFrame;
  gameEscape?: number;
  pointer?: { x: number; y: number; vx: number; vy: number; inside: boolean };
  rect: { left: number; top: number; width: number; height: number; right: number; bottom: number };
  pageRect?: { left: number; top: number; width: number; height: number; right: number; bottom: number };
}

/** DOM only. Three uses this rect to render the existing scene into this window. */
export class SiteHighlights {
  private started: number | null = null;
  private completed = false;
  private lockY: number | null = null;
  private lastNow = 0;
  private hiddenAt: number | null = null;
  private readonly view: Window;
  private readonly section: HTMLElement;
  private readonly target: HTMLElement;
  private readonly portal: HTMLElement;
  private readonly content: HTMLElement;
  private readonly curtain: HTMLElement;
  private readonly headingPortal: HTMLElement;
  private readonly sourceCopy: HTMLElement;
  private lastShot: HighlightShot | null = null;
  private readonly game = new GameSequenceDirector();
  private gameFrame: GameSequenceFrame | null = null;
  private gameHoldMs = 0;
  private gameEscapeAt: number|null = null;
  private gameEscape = 0;
  private readonly originalSlot: string;
  private pointer = { x: 0, y: 0, vx: 0, vy: 0, inside: false };
  private pointerAt = 0;
  private cinemaVisit=-1;
  private readonly pointerMove = (event: PointerEvent): void => {
    if (!this.completed) return;
    const box = this.target.getBoundingClientRect();
    const x = (event.clientX - box.left) / box.width;
    const y = (event.clientY - box.top) / box.height;
    // Hero input uses DOM y-down coordinates, not WebGL's y-up NDC.
    const now=this.view.performance.now(),dt=Math.max(12,Math.min(90,now-this.pointerAt))/1000;
    const nx=x*2-1,ny=y*2-1;
    this.pointer = {x:nx,y:ny,vx:Math.max(-1.65,Math.min(1.65,(nx-this.pointer.x)/dt)),vy:Math.max(-1.65,Math.min(1.65,(ny-this.pointer.y)/dt)),inside:x>=0&&x<=1&&y>=0&&y<=1};
    this.pointerAt=now;
  };
  private readonly preventScroll = (event: Event): void => {
    if (this.lockY !== null) event.preventDefault();
  };
  private readonly keydown = (event: KeyboardEvent): void => {
    if (this.lockY === null) return;
    if (event.key === "Escape") { this.completed = true; this.unlock(); return; }
    if (["ArrowDown", "ArrowUp", "PageDown", "PageUp", "Home", "End", " "].includes(event.key)) event.preventDefault();
  };
  private readonly holdScroll = (): void => {
    if (this.lockY !== null && Math.abs(this.view.scrollY - this.lockY) > 1) this.view.scrollTo({ top: this.lockY, behavior: "instant" });
  };
  private readonly visibility = (): void => {
    if(this.view.__BB_CINEMA__?.active)return;
    if (this.section.ownerDocument.hidden) this.hiddenAt = this.lastNow;
    else if (this.hiddenAt !== null && this.started !== null) {
      this.started += Math.max(0, this.view.performance.now() - this.hiddenAt);
      this.hiddenAt = null;
    }
  };

  constructor(private readonly root: ParentNode) {
    this.section = root.querySelector<HTMLElement>("#uses-site")!;
    this.view = this.section.ownerDocument.defaultView!;
    this.target = this.section.querySelector<HTMLElement>('[data-proof-slot="site-self"]')!;
    this.originalSlot = this.target.innerHTML;
    this.target.innerHTML = '<span class="uses-highlights-window" aria-hidden="true"></span>';
    this.section.dataset.highlights = "ready";
    this.sourceCopy = this.section.querySelector<HTMLElement>(".marketing-copy")!;
    this.sourceCopy.style.opacity='0';this.section.style.setProperty('--highlights-title','0');
    // The route is translated by tens of thousands of pixels and the pinned
    // block counter-translates it. Keep its visible title in the same stable
    // viewport layer as the miniature; the semantic source remains in place.
    this.headingPortal = this.sourceCopy.cloneNode(true) as HTMLElement;
    this.headingPortal.className = "uses-highlights-heading";
    this.headingPortal.removeAttribute("data-site-live-fragment");
    this.headingPortal.setAttribute("aria-hidden", "true");
    for (const node of [this.headingPortal, ...this.headingPortal.querySelectorAll<HTMLElement>("[id]")]) node.removeAttribute("id");
    this.headingPortal.hidden = true;
    this.section.ownerDocument.body.appendChild(this.headingPortal);
    this.portal = this.section.ownerDocument.createElement("div");
    this.portal.className = "uses-highlights-portal";
    this.portal.setAttribute("aria-hidden", "true");
    this.portal.innerHTML = '<div class="uses-highlights-content"></div><div class="uses-highlights-curtain"></div>';
    this.content = this.portal.firstElementChild as HTMLElement;
    this.curtain = this.portal.lastElementChild as HTMLElement;
    this.section.ownerDocument.body.appendChild(this.portal);
    this.section.ownerDocument.addEventListener("visibilitychange", this.visibility);
    this.view.addEventListener("pointermove", this.pointerMove, { passive: true });
  }

  /*
   * `scrubMs` — время раздела, выведенное из прокрутки, а не из настенных часов.
   * В этом режиме блок никогда не запирает страницу: замок писался для варианта,
   * где разворот окна идёт 37,4 секунды и его нельзя перемотать. На телефоне
   * прокрутка — единственный способ управления, и отнять её значит запереть
   * человека внутри одного раздела больше чем на полминуты.
   */
  render(active: boolean, now: number, reduced: boolean, pendingHeading = false, scrubMs: number | null = null): HighlightPresentation | null {
    const cinema=this.view.__BB_CINEMA__;
    if(cinema?.active){
      if(this.cinemaVisit!==cinema.state.visit){this.cinemaVisit=cinema.state.visit;this.started=null;this.completed=false;this.lastShot=null;this.gameFrame=null;this.gameHoldMs=0;this.gameEscapeAt=null;this.gameEscape=0;this.sourceCopy.style.opacity='0';this.section.style.setProperty('--highlights-title','0');this.section.dataset.highlights='ready';delete this.sourceCopy.dataset.highlightHeading;}
      if(cinema.chapter.id!=='uses-site'){this.sourceCopy.style.opacity='0';this.section.style.setProperty('--highlights-title','0');this.portal.hidden=true;this.headingPortal.hidden=true;this.unlock();return null;}
    }
    const delta = Math.max(0, Math.min(250, now - this.lastNow));
    this.lastNow = now;
    const pageBox = this.section.querySelector<HTMLElement>(".marketing-inner")!.getBoundingClientRect();
    const departing = this.completed && pageBox.top < this.view.innerHeight && pageBox.bottom > 0;
    if (!active && !departing) {
      if(this.started!==null && !this.completed) this.started += delta;
      this.portal.hidden = true;
      this.unlock();
      // A large scroll can move the previous application fully offscreen while
      // its cube still completes the exit. The next title may already be read,
      // but no highlight clock, lock or world ownership starts at this point.
      if (pendingHeading && !this.completed && !cinema?.active) {
        this.section.dataset.highlights = "pending";
        this.target.style.opacity = "0";
        this.presentHeading("pending", 1, 0);
        return null;
      }
      this.headingPortal.hidden = true;
      // The semantic heading owns its geometry. It remains visible on an
      // incoming/reverse edge even before the nearest-section selector flips.
      // A future chapter must not reveal its title while its preceding actor
      // still owns the scene. Only an active presentation may expose it.
      // A return from a later section keeps the already watched finale; scrolling
      // back before this block resets it, so a deliberate second visit can replay.
      if (this.section.getBoundingClientRect().top > this.view.innerHeight) {
        this.started = null; this.completed = false; this.lastShot = null;
        this.section.dataset.highlights = "ready";
        this.section.style.setProperty("--highlights-copy-opacity", "1");
        this.section.style.setProperty("--highlights-caption", "0");
        this.target.style.opacity = "0";
      }
      return null;
    }
    if (this.started === null) {
      this.started = scrubMs === null ? now : 0;
      if (!reduced && scrubMs === null) this.lock();
    }
    // A slow GPU must not cut away before the source simulation has landed and
    // revealed the product. Keep the last half-second for the actual fade.
    if (scrubMs === null && !reduced && !this.completed && this.lastShot === "game"
      && now - this.started >= HIGHLIGHT_CUTS[4] - 500
      && ((this.gameFrame?.canonical?.exit ?? 0) < 1 || this.gameEscape<.42)) {
      this.started += delta;
      this.gameHoldMs += delta;
    }
    const elapsed = scrubMs !== null ? scrubMs
      : this.completed || reduced ? HIGHLIGHT_CUTS[6] : now - this.started;
    const state = sampleHighlight(reduced ? HIGHLIGHT_CUTS[6] : elapsed);
    if (scrubMs !== null) this.completed = !state.locked;
    else if (!state.locked) { this.completed = true; this.unlock(); }
    const aspect = String(this.view.innerWidth / this.view.innerHeight);
    this.section.style.setProperty("--highlight-viewport-aspect", aspect);
    this.content.style.setProperty("--highlight-viewport-aspect", aspect);
    const box = this.target.getBoundingClientRect();
    // The end of the expansion matches the actual page composition. Hand the
    // miniature back to its real DOM anchor, not to a permanently fixed clone.
    const e = state.shot === "complete" ? 0 : state.expansion;
    const width = box.width + (this.view.innerWidth - box.width) * e;
    const height = box.height + (this.view.innerHeight - box.height) * e;
    // After takeover this IS the page: follow its physical departure, rather
    // than leaving a fixed overlay that suddenly disappears at the next block.
    const left = box.left * (1 - e), top = box.top * (1 - e);
    const rect = { left, top, width, height, right: left + width, bottom: top + height };
    this.section.dataset.highlights = state.shot;
    this.section.style.setProperty('--highlights-title',String(reduced?1:smooth(state.elapsed/1000)));
    this.section.style.setProperty("--highlights-copy-opacity", String(highlightCopyPresence(state)));
    this.section.style.setProperty("--highlights-caption", String(smooth((state.elapsed - 2550) / 650)));
    this.presentHeading(state.shot, highlightCopyPresence(state), smooth((state.elapsed - 2550) / 650));
    this.target.style.opacity = state.shot === "reading" ? "0" : "1";
    this.portal.hidden = state.shot === "reading";
    this.portal.dataset.shot = state.shot;
    const sx=width/this.view.innerWidth,sy=height/this.view.innerHeight;
    this.portal.style.left = '0px'; this.portal.style.top = '0px';
    this.portal.style.width = `${this.view.innerWidth}px`; this.portal.style.height = `${this.view.innerHeight}px`;
    this.portal.style.transform = `translate3d(${left}px,${top}px,0) scale(${sx},${sy})`;
    // Keep the raster bounds fixed during expansion; only the compositor transform changes.
    this.portal.style.borderRadius = state.shot === "expand" ? "0px" : "14px";
    this.portal.style.borderWidth = "1px";
    this.content.style.width = `${this.view.innerWidth}px`;
    this.content.style.height = `${this.view.innerHeight}px`;
    this.content.style.setProperty("--highlight-recursion-scale", String(box.width / this.view.innerWidth));
    this.content.style.setProperty("--highlight-recursion-scale-y", String(box.height / this.view.innerHeight));
    this.content.style.transform = 'none';
    this.curtain.style.opacity = "0";
    this.content.style.opacity = String(state.fade);
    if (state.shot !== this.lastShot) {
      this.showShot(state.shot); this.lastShot = state.shot;
      if (state.shot === "game") {
        this.game.reset(now, HIGHLIGHT_GAME_START);
        this.gameFrame = null; this.gameHoldMs = 0;this.gameEscapeAt=null;this.gameEscape=0;
      }
    }
    // The highlight is a condensed viewing: its virtual next gesture comes
    // after the same mandatory single and double jumps, never the encore.
    const gameSequence = state.shot === "game" ? this.game.advance(now,this.gameFrame?.canonical?.showcaseComplete?1:0) : undefined;
    if (gameSequence) this.gameFrame = gameSequence;
    if(gameSequence?.canonical?.exit===1)this.gameEscapeAt??=now;
    if(this.gameEscapeAt!==null)this.gameEscape=clamp((now-this.gameEscapeAt-150)/3000);
    const gameHost = this.content.querySelector<HTMLElement>(".uses-highlight-game");
    if (gameSequence && gameHost) applyGameSequencePresentation(gameHost, gameSequence);
    const pointer={...this.pointer};
    if(now-this.pointerAt>100){pointer.vx=0;pointer.vy=0;}
    return { ...state, rect, pageRect:{left:box.left,top:box.top,width:box.width,height:box.height,right:box.right,bottom:box.bottom}, pointer, gameSequence, gameEscape:this.gameEscape, game: gameSequence?.canonical ?? undefined };
  }

  private presentHeading(shot: HighlightShot | "pending", opacity: number, caption: number): void {
    const copyBox = this.sourceCopy.getBoundingClientRect();
    this.headingPortal.hidden = true;
    this.headingPortal.dataset.shot = shot;
    this.headingPortal.style.left = `${copyBox.left}px`;
    this.headingPortal.style.top = `${copyBox.top}px`;
    this.headingPortal.style.width = `${copyBox.width}px`;
    this.headingPortal.style.opacity = String(opacity);
    this.headingPortal.style.setProperty("--highlights-caption", String(caption));
    this.sourceCopy.style.opacity=String(opacity);
    this.sourceCopy.style.setProperty('--highlights-caption',String(caption));
    this.sourceCopy.dataset.highlightHeading=shot;
  }

  private showShot(shot: HighlightShot): void {
    if (shot === "expand") {
      if (this.lastShot === "self") return;
      shot = "self";
    }
    this.content.replaceChildren();
    let source: Element | null = null;
    if (shot === "hero" || shot === "complete") source = this.section.ownerDocument.querySelector("#lHero");
    if (shot === "game") source = this.root.querySelector("#uses-game > .marketing-inner");
    if (shot === "self") source = this.section.querySelector(".marketing-copy");
    if (!source) return;
    const ui = source.cloneNode(true) as HTMLElement;
    for (const node of [ui, ...ui.querySelectorAll<HTMLElement>("[id]")]) node.removeAttribute("id");
    for (const node of ui.querySelectorAll<HTMLElement>("a,button,input,video")) node.setAttribute("tabindex", "-1");
    ui.classList.add(`uses-highlight-${shot === "complete" ? "hero" : shot}`);
    ui.style.removeProperty("opacity"); ui.style.removeProperty("transform"); ui.style.removeProperty("filter");
    this.content.appendChild(ui);
    const addHeroHint=(hero:HTMLElement)=>{
      const hint=this.section.ownerDocument.querySelector<HTMLElement>('.cinema-start')?.cloneNode(true) as HTMLElement|undefined;
      if(hint){hint.hidden=false;hint.style.position='absolute';hint.tabIndex=-1;hero.append(hint);}
    };
    if(shot==='hero'||shot==='complete')addHeroHint(ui);
    if (shot === "self") {
      const nested = this.section.ownerDocument.createElement("div");
      nested.className = "uses-highlight-recursion";
      const hero = this.section.ownerDocument.querySelector("#lHero")!.cloneNode(true) as HTMLElement;
      for (const node of [hero, ...hero.querySelectorAll<HTMLElement>("[id]")]) node.removeAttribute("id");
      hero.classList.add("uses-highlight-hero");
      addHeroHint(hero);
      nested.appendChild(hero); this.content.appendChild(nested);
    }
  }

  private lock(): void {
    if(this.view.__BB_CINEMA__?.active||this.view.innerWidth<700)return;
    this.lockY = this.view.scrollY;
    this.view.addEventListener("wheel", this.preventScroll, { passive: false });
    this.view.addEventListener("touchmove", this.preventScroll, { passive: false });
    this.view.addEventListener("keydown", this.keydown);
    this.view.addEventListener("scroll", this.holdScroll);
  }
  private unlock(): void {
    this.lockY = null;
    this.view.removeEventListener("wheel", this.preventScroll);
    this.view.removeEventListener("touchmove", this.preventScroll);
    this.view.removeEventListener("keydown", this.keydown);
    this.view.removeEventListener("scroll", this.holdScroll);
  }
  destroy(): void {
    this.unlock(); this.portal.remove(); this.headingPortal.remove(); this.target.innerHTML = this.originalSlot;
    delete this.section.dataset.highlights;
    this.section.ownerDocument.removeEventListener("visibilitychange", this.visibility);
    this.view.removeEventListener("pointermove", this.pointerMove);
  }
}
